import React from 'react'
import { ClientOnly } from 'remix-utils/client-only'
import ElevationChart from '#app/components/elevation-chart.tsx'
import LeafletMap from '#app/components/leaflet-map.client.tsx'
import TrailCard from '#app/components/trail-card.tsx'
import TrailHelp from '#app/components/trail-help.tsx'
import { ScrollArea } from '#app/components/ui/scroll-area.tsx'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '#app/components/ui/tabs.tsx'
import { type MobileLayoutProps } from './trail-capture-layouts.tsx'

export default function TrailCaptureMobileLayout({
	trail,
	trailCapture,
	captureData,
	currentFrameId,
	positions,
	mapMarkers,
	blip,
	onNavigate,
	onPositionChange,
	activeTab,
	onTabChange,
	children,
}: MobileLayoutProps) {
	return (
		<div className="h-full min-h-0">
			<Tabs
				value={activeTab}
				onValueChange={(value) =>
					onTabChange(
						value as 'image' | 'chart' | 'map' | 'description' | 'help',
					)
				}
				className="flex h-full min-h-0 flex-col"
			>
				<TabsList
					className="mt-2 w-full shrink-0 overflow-x-auto border-t px-2"
					style={{ scrollbarWidth: 'none' }}
				>
					<TabsTrigger
						value="description"
						className="ml-8 p-2 text-center focus:ring-2 focus:outline-hidden"
					>
						Description
					</TabsTrigger>
					<TabsTrigger
						value="image"
						className="p-2 text-center focus:ring-2 focus:outline-hidden"
					>
						Image
					</TabsTrigger>
					<TabsTrigger
						value="chart"
						className="p-2 text-center focus:ring-2 focus:outline-hidden"
					>
						Elevation
					</TabsTrigger>
					<TabsTrigger
						value="map"
						className="p-2 text-center focus:ring-2 focus:outline-hidden"
					>
						Map
					</TabsTrigger>
					<TabsTrigger
						value="help"
						className="p-2 text-center focus:ring-2 focus:outline-hidden"
					>
						Help
					</TabsTrigger>
				</TabsList>

				<div className="min-h-0 flex-1 overflow-hidden">
					<TabsContent
						value="description"
						className="h-full min-h-0 overflow-hidden"
					>
						<ScrollArea className="h-full overflow-y-auto">
							<TrailCard
								key={trail.id}
								trail={trail}
								trailCapture={trailCapture}
							/>
						</ScrollArea>
					</TabsContent>

					<TabsContent value="image" className="h-full min-h-0 overflow-hidden">
						<div className="relative h-full min-h-0 w-full">{children}</div>
					</TabsContent>

					<TabsContent value="map" className="h-full min-h-0 overflow-hidden">
						<div className="h-full w-full">
							<ClientOnly
								fallback={<div id="skeleton" className="h-full w-full" />}
							>
								{() =>
									captureData ? (
										<LeafletMap
											className="h-full w-full"
											center={captureData.center}
											bounds={captureData.bounds}
											positions={positions}
											markers={mapMarkers}
											blip={blip}
											navigateTo={onNavigate}
										/>
									) : (
										<div className="h-full w-full animate-pulse rounded-md bg-gray-200" />
									)
								}
							</ClientOnly>
						</div>
					</TabsContent>

					<TabsContent value="chart" className="h-full min-h-0 overflow-hidden">
						<div className="h-full w-full">
							{captureData ? (
								<ElevationChart
									trailCapture={trailCapture}
									data={captureData}
									currentFrameId={currentFrameId}
									onPositionChange={onPositionChange}
									navigateTo={onNavigate}
								/>
							) : (
								<div className="h-full w-full animate-pulse rounded-md bg-gray-200" />
							)}
						</div>
					</TabsContent>

					<TabsContent value="help" className="h-full min-h-0 overflow-hidden">
						<ScrollArea className="h-full overflow-y-auto">
							<TrailHelp />
						</ScrollArea>
					</TabsContent>
				</div>
			</Tabs>
		</div>
	)
}
